import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_LANG,
  isLang,
  localizePath,
  parsePath,
  type Lang,
} from "@/i18n/locales";

const STORAGE_KEY = "devcore_lang";
const BOT_RE =
  /bot|crawl|spider|slurp|bingpreview|gptbot|claudebot|perplexity|facebookexternal|embedly|quora|whatsapp|telegram|lighthouse|headless/i;

/**
 * First-visit language picker. When a user lands on an un-prefixed (English)
 * URL without a stored preference, we resolve their country server-side and
 * redirect to the matching language (Germany → de, Cyprus/Greece → el, else en).
 * A stored preference always wins; crawlers and prerender are never redirected
 * so the static language URLs stay stable for SEO.
 */
const GeoRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // Never redirect the prerender pass or known crawlers.
    if (typeof window !== "undefined" && (window as { __PRERENDER__?: boolean }).__PRERENDER__) return;
    if (typeof navigator !== "undefined" && BOT_RE.test(navigator.userAgent)) return;

    // Don't touch admin / utility routes. `/book` IS localized so the
    // customer can schedule in their own language — keep it redirectable.
    if (/^\/(admin|unsubscribe)(\/|$)/.test(location.pathname)) return;

    const { lang, path } = parsePath(location.pathname);
    // If the URL already carries a language prefix, the user has an explicit
    // choice — respect it and persist it.
    if (lang !== DEFAULT_LANG) {
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch {
        /* ignore */
      }
      return;
    }

    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }

    const go = (target: Lang) => {
      try {
        localStorage.setItem(STORAGE_KEY, target);
      } catch {
        /* ignore */
      }
      if (target !== DEFAULT_LANG) {
        navigate(localizePath(path, target) + location.hash, { replace: true });
      }
    };

    if (stored && isLang(stored)) {
      go(stored);
      return;
    }

    // No stored preference → resolve by geolocation, once.
    (async () => {
      try {
        const { data } = await supabase.functions.invoke<{ locale: Lang }>(
          "detect-locale"
        );
        const target = data?.locale && isLang(data.locale) ? data.locale : DEFAULT_LANG;
        go(target);
      } catch {
        go(DEFAULT_LANG);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default GeoRedirect;
